import { useCallback, useState } from 'react';
import { useToastStore } from '@/stores/toast-store';
import { getFileErrorMessage } from '@/utils/get-error-message';

/**
 * Generic authorised-file download core (LR-508 / T5).
 *
 * Extracted from `useDownloadStatement` (LR-509) so both a generated statement
 * (export by id) and an MTD preview (export by params, no id) reuse the same
 * blob -> browser-save logic - DRY. Callers pass a `fetchBlob` thunk that hits
 * whichever export endpoint they need and returns the blob + the server-set
 * filename; `fallbackName` is used only when the server omits `Content-Disposition`.
 *
 * A bare `<a href>` can't carry the auth cookie/headers, so the file must be
 * fetched through the api layer first. `isDownloading` is a single flag, so any
 * in-flight download disables every button wired to the same hook instance.
 */
export const useBlobDownload = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const addToast = useToastStore((s) => s.addToast);

  const run = useCallback(
    async (
      fetchBlob: () => Promise<{ blob: Blob; filename: string | null }>,
      fallbackName: string,
    ) => {
      setIsDownloading(true);
      setDownloadError(null);
      let objectUrl: string | null = null;
      try {
        const { blob, filename } = await fetchBlob();
        objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        // `||` (not `??`): an empty server filename also falls back to the
        // client-side name, so `link.download` is never blank.
        link.download = filename || fallbackName;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        // `getFileErrorMessage`, not `getErrorMessage`: the export endpoints are
        // fetched as blobs, so an error body arrives as a Blob and has to be read
        // back before it says anything. Worth the unwrap — a document too large
        // to lay out as a PDF is refused with the row count and a pointer to CSV
        // (LR-571), and without this the user is left with a download button that
        // fails for no stated reason.
        //
        // Generic fallback noun: this core is shared by the statement download and
        // the MTD preview download, so it must not say "statement" for a preview.
        const message = await getFileErrorMessage(err, 'Failed to download file');
        setDownloadError(message);
        // The toast carries the same message the caller can read off
        // `downloadError` — today nothing renders that state, so the toast is the
        // only place the reason is seen.
        addToast('error', message);
      } finally {
        // Defer the revoke to the next macrotask: revoking synchronously right
        // after click() can hand WebKit/Safari an empty download (URL torn down
        // before the blob is read). `objectUrl` is null on the pre-createObjectURL
        // error path, so a failed request revokes nothing and never leaks.
        if (objectUrl) {
          const url = objectUrl;
          setTimeout(() => window.URL.revokeObjectURL(url), 0);
        }
        setIsDownloading(false);
      }
    },
    [addToast],
  );

  return { run, isDownloading, downloadError };
};
